// src/pages/SubjectPapers.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { HiOutlineArrowNarrowRight } from "react-icons/hi";
import {
  collection,
  query as fq,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../components/firebase/firebase";
import Loader from "../components/common/Loader";
import { SEO } from "../components/common/SEO";
import SaveButton from "../components/student/SaveButton";

const EXAMS = [
  { title: "First Mid Term", key: "first-mid-term" },
  { title: "Quarterly", key: "quarterly" },
  { title: "Second Mid Term", key: "second-mid-term" },
  { title: "Half Yearly", key: "half-yearly" },
  { title: "Third Mid Term", key: "third-mid-term" },
  { title: "First Revision", key: "first-revision" },
  { title: "Second Revision", key: "second-revision" },
  { title: "Third Revision", key: "third-revision" },
  { title: "Model Board", key: "model-board" },
  { title: "Study Materials", key: "study-materials" },
];

// ✅ No comma operator; clean for `no-sequences`
const EXAM_TITLE = EXAMS.reduce((acc, e) => {
  acc[e.key] = e.title;
  return acc;
}, {});

// Uploads store the board in the `type` field, so it shows up in this URL slot.
const BOARD_TITLE = { state: "State Board", cbse: "CBSE", competitive: "Competitive" };

const SubjectPapers = () => {
  const { type = "all", grade = "all", subject = "all" } = useParams();
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const papersPerPage = 12;

  const prettyType = useMemo(
    () => (type === "all" ? "Papers" : BOARD_TITLE[type] ?? EXAM_TITLE[type] ?? type.replace(/-/g, " ")),
    [type]
  );
  const subjectWords = subject.replace(/-+/g, " ").trim();
  const prettySubject =
    subject !== "all" ? subjectWords.charAt(0).toUpperCase() + subjectWords.slice(1) : "";

  useEffect(() => {
    const fetchPapers = async () => {
      setLoading(true);
      setFetchError("");
      try {
        let base = collection(db, "resources");
        const conds = [];
        if (type !== "all") conds.push(where("type", "==", type));
        if (grade !== "all") conds.push(where("grade", "==", grade));
        if (subject !== "all") conds.push(where("subject", "==", subject));
        const q = conds.length ? fq(base, ...conds) : base;

        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPapers(data);
      } catch (err) {
        console.error("Error fetching papers:", err);
        setFetchError(err?.message || "Failed to load papers.");
      } finally {
        setLoading(false);
      }
    };

    fetchPapers();
  }, [type, grade, subject]);

  // Filter + sort (live as you type)
  const term = searchTerm.trim().toLowerCase();
  const filtered = papers.filter(
    (p) =>
      !term ||
      p.title?.toLowerCase().includes(term) ||
      p.subject?.toLowerCase().includes(term)
  );
  const sorted = filtered.slice().sort((a, b) => {
    const mA = new Date(`${a.month || "January"} 1`).getMonth();
    const mB = new Date(`${b.month || "January"} 1`).getMonth();
    const dateA = new Date(a.year || 0, mA).getTime();
    const dateB = new Date(b.year || 0, mB).getTime();
    return dateB - dateA;
  });

  // Pagination
  const totalPages = Math.ceil(sorted.length / papersPerPage) || 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * papersPerPage;
  const currentPapers = sorted.slice(startIndex, startIndex + papersPerPage);

  if (loading) return <Loader />;

  if (fetchError) return (
    <section className="bg-pink-50 py-10 sm:py-16">
      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 text-center">
        <p className="text-red-500 font-semibold">⚠ Error loading papers</p>
        <p className="text-gray-500 text-sm mt-2">{fetchError}</p>
        <p className="text-gray-400 text-xs mt-1">Check browser console for details (may need a Firestore index).</p>
      </div>
    </section>
  );

  const seoTitle = [prettyType, grade !== "all" ? `Class ${grade}` : "", prettySubject]
    .filter(Boolean).join(" — ");
  const seoDesc = `Free ${seoTitle} question papers${grade !== "all" ? ` for Class ${grade}` : ""} — Tamil Nadu State Board, CBSE & Competitive. View online, free forever on Ravi TestPapers.`;

  return (
    <section className="bg-pink-50 py-10 sm:py-16 overflow-x-hidden">
      <SEO title={seoTitle} description={seoDesc} />
      <div className="max-w-screen-lg mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 leading-snug break-words">
            {prettyType}
            {grade !== "all" && <span> — Class {grade}</span>}
            {prettySubject && <span> — {prettySubject}</span>}
          </h1>
          <p className="mt-2 text-gray-600 text-sm sm:text-base">
            Browse & view free subject-wise practice papers.
          </p>

          {/* Search input only (full-width on mobile) */}
          <div className="mt-3">
            <input
              type="text"
              placeholder="Search papers…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full md:max-w-md px-3 py-2 sm:px-4 sm:py-2.5 bg-white border border-pink-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent text-sm sm:text-base"
            />
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {currentPapers.map((paper, i) => (
            <motion.article
              key={paper.id}
              className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
            >
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  {paper.subject && (
                    <span className="bg-pink-600/90 text-white px-2.5 py-1 rounded-full text-xs sm:text-[0.8rem] capitalize">
                      {paper.subject}
                    </span>
                  )}
                  {(paper.month || paper.year) && (
                    <span className="bg-pink-100 text-pink-800 px-2.5 py-1 rounded-full text-xs sm:text-[0.8rem] ml-auto">
                      {(paper.month || "").toString()} {paper.year || ""}
                    </span>
                  )}
                </div>
                <div className="flex items-start gap-2">
                  <h3 className="flex-1 text-base sm:text-lg font-semibold text-gray-800 line-clamp-2">
                    {paper.title || "Untitled"}
                  </h3>
                  <SaveButton kind="paper" item={paper} className="-mt-1.5 -mr-1.5" />
                </div>
                {paper.refId && (
                  <p className="mt-1.5 text-[11px] font-mono text-gray-400 tracking-wide">
                    #{paper.refId}
                  </p>
                )}
              </div>

              <div className="flex justify-between items-center px-4 py-3 border-t border-pink-100">
                <div className="space-y-1 text-xs sm:text-sm">
                  {paper.referenceLink && (
                    <a
                      href={paper.referenceLink}
                      target="_blank"
                      rel="noreferrer"
                      className="text-pink-600 hover:underline"
                    >
                      View Answers
                    </a>
                  )}
                  <div className="text-gray-500">Views: {paper.views || 0}</div>
                </div>

                {paper.id ? (
                  <NavLink
                    to={`/paper/${paper.id}`}
                    className="flex items-center text-pink-600 hover:text-pink-800 text-sm sm:text-base font-semibold"
                  >
                    View Paper <HiOutlineArrowNarrowRight className="ml-1" />
                  </NavLink>
                ) : (
                  <span className="text-red-500 text-xs sm:text-sm">No link</span>
                )}
              </div>
            </motion.article>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-10 sm:mt-12 flex-wrap gap-2">
            {Array.from({ length: totalPages }, (_, idx) => {
              const page = idx + 1;
              const active = page === safePage;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 sm:px-4 py-2 rounded-full border text-sm sm:text-base ${
                    active
                      ? "bg-pink-600 text-white border-pink-600"
                      : "bg-white text-pink-600 border-pink-200 hover:border-pink-400"
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default SubjectPapers;
